import { GestureArea } from "ll-common-behavior";
import { Player, matchesMediaQuery, mediaQueryListener, throttle } from "ll-common-utilities";
import { animate, inView, scroll, timeline } from "vendor";
/* ─── carousel-navigation ───────────────────────────────────────────── */
var CarouselNavigation = class extends HTMLElement {
  #abortController;
  #allItems;
  constructor() {
    super(...arguments);
    this.#allItems = [];
  }
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel navigation component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    this.#abortController = new AbortController();
    this.#allItems = Array.from(this.querySelectorAll("button"));
    this.#allItems.forEach((button) => button.addEventListener("click", () => this.onButtonClicked(this.items.indexOf(button)), { signal: this.#abortController.signal }));
    this.carousel.addEventListener("carousel:change", (event) => this.onNavigationChange(event.detail.index), { signal: this.#abortController.signal });
    this.carousel.addEventListener("carousel:filter", this.#onCarouselFilter.bind(this), { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  get items() {
    return this.#allItems.filter((item) => !item.hasAttribute("hidden"));
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  get selectedIndex() {
    return this.items.findIndex((button) => button.getAttribute("aria-current") === "true");
  }
  onButtonClicked(newIndex) {
    this.carousel.select(newIndex);
    this.onNavigationChange(newIndex);
  }
  onNavigationChange(newIndex) {
    this.items.forEach((button, index) => button.setAttribute("aria-current", newIndex === index ? "true" : "false"));
    if (this.hasAttribute("align-selected") && (this.scrollWidth !== this.clientWidth || this.scrollHeight !== this.clientHeight)) {
      this.scrollTo({
        left: this.items[newIndex].offsetLeft - this.clientWidth / 2 + this.items[newIndex].clientWidth / 2,
        top: this.items[newIndex].offsetTop - this.clientHeight / 2 + this.items[newIndex].clientHeight / 2,
        behavior: matchesMediaQuery("motion-safe") ? "smooth" : "auto"
      });
    }
  }
  #onCarouselFilter(event) {
    this.#allItems.forEach((item, index) => {
      item.toggleAttribute("hidden", (event.detail.filteredIndexes || []).includes(index));
    });
  }
};
var CarouselPrevButton = class extends HTMLElement {
  #abortController;
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel prev button component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    this.#abortController = new AbortController();
    this.addEventListener("click", () => this.carousel.previous(), { signal: this.#abortController.signal });
    this.carousel.addEventListener("scroll:edge-nearing", (event) => this.firstElementChild.disabled = event.detail.position === "start", { signal: this.#abortController.signal });
    this.carousel.addEventListener("scroll:edge-leaving", (event) => this.firstElementChild.disabled = event.detail.position === "start" ? false : this.firstElementChild.disabled, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
};
var CarouselNextButton = class extends HTMLElement {
  #abortController;
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel next button component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    this.#abortController = new AbortController();
    this.addEventListener("click", () => this.carousel.next(), { signal: this.#abortController.signal });
    this.carousel.addEventListener("scroll:edge-nearing", (event) => this.firstElementChild.disabled = event.detail.position === "end", { signal: this.#abortController.signal });
    this.carousel.addEventListener("scroll:edge-leaving", (event) => this.firstElementChild.disabled = event.detail.position === "end" ? false : this.firstElementChild.disabled, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
};
if (!window.customElements.get("carousel-prev-button")) {
  window.customElements.define("carousel-prev-button", CarouselPrevButton);
}
if (!window.customElements.get("carousel-next-button")) {
  window.customElements.define("carousel-next-button", CarouselNextButton);
}
if (!window.customElements.get("carousel-navigation")) {
  window.customElements.define("carousel-navigation", CarouselNavigation);
}
/* ─── effect-carousel ───────────────────────────────────────────────── */
var EffectCarousel = class extends HTMLElement {
  #gestureArea;
  #listenersAbortController;
  #player;
  #preventInitialTransition;
  #targetIndex;
  constructor() {
    super();
    this.#targetIndex = 0;
    this.#preventInitialTransition = false;
    this.#setupListeners();
    inView(this, () => this.onBecameVisible());
    this.addEventListener("carousel:settle", (event) => {
      this.allCells.forEach((cell) => cell.classList.toggle("ll-is-selected", cell === event.detail.cell));
    });
  }
  connectedCallback() {
    this.#targetIndex = Math.max(0, this.cells.findIndex((item) => item.classList.contains("ll-is-selected")));
    inView(this, () => this.#preloadImages());
  }
  get allowSwipe() {
    return this.hasAttribute("allow-swipe");
  }
  get cellSelector() {
    return this.hasAttribute("cell-selector") ? this.getAttribute("cell-selector") : null;
  }
  get allCells() {
    return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
  }
  get cells() {
    return this.allCells.filter((cell) => !cell.hasAttribute("hidden"));
  }
  get selectedCell() {
    return this.cells[this.selectedIndex];
  }
  get selectedIndex() {
    return this.#targetIndex;
  }
  get player() {
    return this.#player;
  }
  previous({ instant = false } = {}) {
    return this.select((this.selectedIndex - 1 + this.cells.length) % this.cells.length, { instant, direction: "previous" });
  }
  next({ instant = false } = {}) {
    return this.select((this.selectedIndex + 1 + this.cells.length) % this.cells.length, { instant, direction: "next" });
  }
  async select(index, { instant = false, direction = null } = {}) {
    if (!(index in this.cells)) {
      return Promise.resolve();
    }
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: this.cells[index] } }));
    if (index === this.selectedIndex) {
      return Promise.resolve();
    }
    this.#player?.pause();
    const [fromSlide, toSlide] = [this.selectedCell, this.cells[index]];
    direction ??= index > this.selectedIndex ? "next" : "previous";
    this.#targetIndex = index;
    this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index, cell: this.cells[index] } }));
    const animationControls = this.createOnChangeAnimationControls(fromSlide, toSlide, { direction });
    if ("leaveControls" in animationControls && "enterControls" in animationControls) {
      const leaveAnimationControls = animationControls.leaveControls();
      if (instant) {
        leaveAnimationControls.finish();
      }
      await leaveAnimationControls.finished;
      this.#player?.resume(true);
      fromSlide.classList.remove("ll-is-selected");
      toSlide.classList.add("ll-is-selected");
      const enterAnimationControls = animationControls.enterControls();
      if (instant) {
        enterAnimationControls.finish();
      }
      await enterAnimationControls.finished;
    } else {
      if (instant) {
        animationControls.finish();
      }
      this.#player?.resume(true);
      toSlide.classList.add("ll-is-selected");
      await animationControls.finished;
      fromSlide.classList.remove("ll-is-selected");
    }
    this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index, cell: this.cells[index] } }));
  }
  filter(indexes = []) {
    this.allCells.forEach((cell, index) => {
      cell.toggleAttribute("hidden", indexes.includes(index));
    });
    this.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes: indexes } }));
  }
  async onBecameVisible() {
    const animationControls = await this.createOnBecameVisibleAnimationControls(this.selectedCell);
    [this.selectedCell, ...this.selectedCell.querySelectorAll("[reveal-on-scroll]")].forEach((element) => {
      element.removeAttribute("reveal-on-scroll");
    });
    if (this.#preventInitialTransition && typeof animationControls.finish === "function") {
      animationControls.finish();
    }
    return animationControls.finished.then(() => {
      this.#player?.resume(true);
      this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
    });
  }
  createOnBecameVisibleAnimationControls(toSlide) {
    return animate(toSlide, {}, { duration: 0 });
  }
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    return timeline([
      [fromSlide, { opacity: [1, 0] }, { duration: 0.3 }],
      [toSlide, { opacity: [0, 1] }, { duration: 0.3, at: "<" }]
    ]);
  }
  cleanUpAnimations() {
    this.allCells.forEach((cell) => {
      cell.style.removeProperty("opacity");
      cell.style.removeProperty("visibility");
    });
  }
  #setupListeners() {
    if (this.hasAttribute("disabled-on")) {
      mediaQueryListener(this.getAttribute("disabled-on"), (event) => {
        if (event.matches) {
          this.#listenersAbortController?.abort();
          this.cleanUpAnimations();
        } else {
          this.#setupListeners();
        }
      });
      if (matchesMediaQuery(this.getAttribute("disabled-on"))) {
        return;
      }
    }
    this.#listenersAbortController = new AbortController();
    const listenerOptions = { signal: this.#listenersAbortController.signal };
    if (Shopify.designMode) {
      this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => (this.#preventInitialTransition = event.detail.load), listenerOptions);
    }
    if (this.allCells.length > 1) {
      this.addEventListener("carousel:change", this.#preloadImages);
      if (this.allowSwipe) {
        this.#gestureArea = new GestureArea(this, { signal: this.#listenersAbortController.signal });
        this.addEventListener("swipeleft", this.next, listenerOptions);
        this.addEventListener("swiperight", this.previous, listenerOptions);
      }
      if (!this.hasAttribute("disable-keyboard-navigation")) {
        this.tabIndex = 0;
        this.addEventListener("keydown", this.#onKeyboardNavigation, listenerOptions);
      }
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.cells.indexOf(event.target), { instant: event.detail.load }), listenerOptions);
      }
      if (this.hasAttribute("autoplay")) {
        this.#player ?? (this.#player = new Player(this.getAttribute("autoplay") ?? 5));
        this.#player.addEventListener("player:end", this.next.bind(this), listenerOptions);
        if (Shopify.designMode) {
          this.addEventListener("shopify:block:select", () => this.#player.stop(), listenerOptions);
          this.addEventListener("shopify:block:deselect", () => this.#player.start(), listenerOptions);
        }
      }
    }
  }
  #onKeyboardNavigation(event) {
    if (event.target !== this) {
      return;
    }
    if (event.code === "ArrowLeft") {
      this.previous();
    } else if (event.code === "ArrowRight") {
      this.next();
    }
  }
  #preloadImages() {
    const previousSlide = this.cells[(this.selectedIndex - 1 + this.cells.length) % this.cells.length], nextSlide = this.cells[(this.selectedIndex + 1 + this.cells.length) % this.cells.length];
    [previousSlide, this.selectedCell, nextSlide].forEach((item) => {
      Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
      Array.from(item.querySelectorAll('video[preload="none"]')).forEach((video) => video.setAttribute("preload", "metadata"));
    });
  }
};
if (!window.customElements.get("effect-carousel")) {
  window.customElements.define("effect-carousel", EffectCarousel);
}
/* ─── scroll-carousel ───────────────────────────────────────────────── */
var ScrollCarousel = class extends HTMLElement {
  #dispatchableScrollEvents;
  #dragPosition;
  #forceChangeEvent;
  #hasPendingProgrammaticScroll;
  #isDragging;
  #onMouseClickListener;
  #onMouseDownListener;
  #onMouseMoveListener;
  #onMouseUpListener;
  #scrollTimeout;
  #targetIndex;
  constructor() {
    super();
    this.#hasPendingProgrammaticScroll = false;
    this.#onMouseDownListener = this.#onMouseDown.bind(this);
    this.#onMouseMoveListener = this.#onMouseMove.bind(this);
    this.#onMouseClickListener = this.#onMouseClick.bind(this);
    this.#onMouseUpListener = this.#onMouseUp.bind(this);
    this.#targetIndex = 0;
    this.#forceChangeEvent = false;
    this.#dragPosition = {};
    this.#isDragging = false;
    this.#dispatchableScrollEvents = { nearingStart: true, nearingEnd: true, leavingStart: true, leavingEnd: true };
    this.#setupListeners();
    new ResizeObserver(this.#onResize.bind(this)).observe(this);
    new MutationObserver(this.#onMutate.bind(this)).observe(this, { subtree: true, attributes: true, attributeFilter: ["hidden"] });
  }
  connectedCallback() {
    this.#targetIndex = Math.max(0, this.cells.findIndex((item) => item.classList.contains("is-initial")));
    if (this.#targetIndex > 0) {
      this.select(this.#targetIndex, { instant: true });
    }
    if (this.adaptiveHeight) {
      this.#adaptHeight();
    }
    inView(this, () => this.#preloadImages());
  }
  disconnectedCallback() {
    this.removeEventListener("mousemove", this.#onMouseMoveListener);
    document.removeEventListener("mouseup", this.#onMouseUpListener);
  }
  get cellSelector() {
    return this.hasAttribute("cell-selector") ? this.getAttribute("cell-selector") : null;
  }
  get allCells() {
    return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
  }
  get cells() {
    return this.allCells.filter((cell) => !cell.hasAttribute("hidden"));
  }
  get selectedCell() {
    return this.cells[this.selectedIndex];
  }
  get selectedIndex() {
    return this.#targetIndex;
  }
  get cellAlign() {
    const scrollSnapAlign = getComputedStyle(this.cells[0]).scrollSnapAlign;
    return scrollSnapAlign === "none" ? "center" : scrollSnapAlign;
  }
  get groupCells() {
    if (this.hasAttribute("group-cells")) {
      const number = parseInt(this.getAttribute("group-cells"));
      return isNaN(number) ? Math.floor(this.clientWidth / this.cells[0].clientWidth) : number;
    } else {
      return 1;
    }
  }
  get adaptiveHeight() {
    return this.hasAttribute("adaptive-height");
  }
  get isScrollable() {
    const differenceWidth = this.scrollWidth - this.clientWidth;
    const differenceHeight = this.scrollHeight - this.clientHeight;
    return differenceWidth > 1 || differenceHeight > 1;
  }
  previous({ instant = false } = {}) {
    this.select(Math.max(this.#targetIndex - this.groupCells, 0), { instant });
  }
  next({ instant = false } = {}) {
    this.select(Math.min(this.#targetIndex + this.groupCells, this.cells.length - 1), { instant });
  }
  select(index, { instant = false } = {}) {
    if (!(index in this.cells)) {
      return;
    }
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: this.cells[index] } }));
    if ("checkVisibility" in this && this.checkVisibility() || this.offsetWidth > 0 && this.offsetHeight > 0) {
      const targetScrollLeft = this.#calculateLeftScroll(this.cells[index]);
      if (this.scrollLeft !== targetScrollLeft) {
        this.#updateTargetIndex(index);
        this.#hasPendingProgrammaticScroll = true;
        this.scrollTo({ left: targetScrollLeft, behavior: instant ? "auto" : "smooth" });
      } else {
        this.#updateTargetIndex(this.#calculateClosestIndexToAlignment());
      }
    } else {
      this.#targetIndex = index;
      this.#forceChangeEvent = true;
    }
  }
  filter(indexes = []) {
    this.allCells.forEach((cell, index) => {
      cell.toggleAttribute("hidden", indexes.includes(index));
    });
    this.#forceChangeEvent = true;
    this.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes: indexes } }));
  }
  #setupListeners() {
    if (this.allCells.length > 1) {
      this.addEventListener("carousel:change", this.#preloadImages);
      this.addEventListener("scroll", throttle(this.#onScroll.bind(this)));
      this.addEventListener("scrollend", this.#onScrollEnd);
      if (this.hasAttribute("allow-drag")) {
        const mediaQuery = window.matchMedia("screen and (pointer: fine)");
        mediaQuery.addEventListener("change", (event) => {
          if (event.matches) {
            this.addEventListener("mousedown", this.#onMouseDownListener);
          } else {
            this.removeEventListener("mousedown", this.#onMouseDownListener);
          }
        });
        if (mediaQuery.matches) {
          this.addEventListener("mousedown", this.#onMouseDownListener);
        }
      }
      if (this.adaptiveHeight) {
        this.addEventListener("carousel:settle", this.#adaptHeight);
      }
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.cells.indexOf(event.target), { instant: event.detail.load }));
      }
    }
  }
  #updateTargetIndex(newValue) {
    if (newValue === this.#targetIndex && !this.#forceChangeEvent) {
      return;
    }
    this.#targetIndex = newValue;
    this.#forceChangeEvent = false;
    this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index: newValue, cell: this.cells[newValue] } }));
  }
  #onScroll() {
    const scrollEdgeThreshold = 100, normalizedScrollLeft = Math.round(Math.abs(this.scrollLeft));
    if (normalizedScrollLeft < scrollEdgeThreshold && this.#dispatchableScrollEvents["nearingStart"]) {
      this.dispatchEvent(new CustomEvent("scroll:edge-nearing", { detail: { position: "start" } }));
      this.#dispatchableScrollEvents["nearingStart"] = false;
      this.#dispatchableScrollEvents["leavingStart"] = true;
    }
    if (normalizedScrollLeft >= scrollEdgeThreshold && this.#dispatchableScrollEvents["leavingStart"]) {
      this.dispatchEvent(new CustomEvent("scroll:edge-leaving", { detail: { position: "start" } }));
      this.#dispatchableScrollEvents["leavingStart"] = false;
      this.#dispatchableScrollEvents["nearingStart"] = true;
    }
    if (this.scrollWidth - this.clientWidth < normalizedScrollLeft + scrollEdgeThreshold && this.#dispatchableScrollEvents["nearingEnd"]) {
      this.dispatchEvent(new CustomEvent("scroll:edge-nearing", { detail: { position: "end" } }));
      this.#dispatchableScrollEvents["nearingEnd"] = false;
      this.#dispatchableScrollEvents["leavingEnd"] = true;
    }
    if (this.scrollWidth - this.clientWidth >= normalizedScrollLeft + scrollEdgeThreshold && this.#dispatchableScrollEvents["leavingEnd"]) {
      this.dispatchEvent(new CustomEvent("scroll:edge-leaving", { detail: { position: "end" } }));
      this.#dispatchableScrollEvents["leavingEnd"] = false;
      this.#dispatchableScrollEvents["nearingEnd"] = true;
    }
    if (!("onscrollend" in window)) {
      clearTimeout(this.#scrollTimeout);
      this.#scrollTimeout = setTimeout(() => {
        this.dispatchEvent(new CustomEvent("scrollend", { bubbles: true }));
      }, 75);
    }
    if (this.#hasPendingProgrammaticScroll) {
      return;
    }
    this.#updateTargetIndex(this.#calculateClosestIndexToAlignment());
  }
  #onScrollEnd() {
    this.#hasPendingProgrammaticScroll = false;
    if (!this.#isDragging) {
      this.style.removeProperty("scroll-snap-type");
    }
    this.#updateTargetIndex(this.#calculateClosestIndexToAlignment());
    this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
  }
  #calculateLeftScroll(cell) {
    let scrollLeft;
    switch (this.cellAlign) {
      case "start":
        scrollLeft = document.dir === "ltr" ? cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0) : cell.offsetLeft + cell.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0);
        break;
      case "center":
        scrollLeft = Math.round(cell.offsetLeft - this.clientWidth / 2 + cell.clientWidth / 2);
        break;
      case "end":
        scrollLeft = document.dir === "ltr" ? cell.offsetLeft + cell.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0) : cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0);
        break;
    }
    return document.dir === "ltr" ? Math.min(Math.max(scrollLeft, 0), this.scrollWidth - this.clientWidth) : Math.min(Math.max(scrollLeft, this.clientWidth - this.scrollWidth), 0);
  }
  #calculateClosestIndexToAlignment() {
    let cellAlign = this.cellAlign, offsetAccumulators, targetPoint;
    if (cellAlign === "center") {
      offsetAccumulators = this.cells.map((cell) => Math.round(cell.offsetLeft + cell.clientWidth / 2));
      targetPoint = Math.round(this.scrollLeft + this.clientWidth / 2);
    } else if (cellAlign === "start" && document.dir === "ltr" || cellAlign === "end" && document.dir === "rtl") {
      offsetAccumulators = this.cells.map((cell) => cell.offsetLeft);
      targetPoint = this.scrollLeft;
    } else {
      offsetAccumulators = this.cells.map((cell) => cell.offsetLeft + cell.clientWidth);
      targetPoint = this.scrollLeft + this.clientWidth;
    }
    return offsetAccumulators.indexOf(offsetAccumulators.reduce((prev, curr) => Math.abs(curr - targetPoint) < Math.abs(prev - targetPoint) ? curr : prev));
  }
  #onMouseDown(event) {
    this.#dragPosition = {
      left: this.scrollLeft,
      top: this.scrollTop,
      x: event.clientX,
      y: event.clientY
    };
    this.#isDragging = true;
    this.style.setProperty("scroll-snap-type", "none");
    this.addEventListener("mousemove", this.#onMouseMoveListener);
    this.addEventListener("click", this.#onMouseClickListener, { once: true });
    document.addEventListener("mouseup", this.#onMouseUpListener);
  }
  #onMouseMove(event) {
    event.preventDefault();
    const [dx, dy] = [event.clientX - this.#dragPosition.x, event.clientY - this.#dragPosition.y];
    this.scrollTop = this.#dragPosition.top - dy;
    this.scrollLeft = this.#dragPosition.left - dx;
  }
  #onMouseClick(event) {
    if (event.clientX - this.#dragPosition.x !== 0) {
      event.preventDefault();
    }
  }
  #onMouseUp(event) {
    this.#isDragging = false;
    if (event.clientX - this.#dragPosition.x === 0) {
      this.style.removeProperty("scroll-snap-type");
    } else if (!this.#hasPendingProgrammaticScroll) {
      this.scrollTo({ left: this.#calculateLeftScroll(this.selectedCell), behavior: "smooth" });
    }
    this.removeEventListener("mousemove", this.#onMouseMoveListener);
    document.removeEventListener("mouseup", this.#onMouseUpListener);
  }
  #onResize() {
    if (this.selectedIndex !== this.#calculateClosestIndexToAlignment()) {
      this.select(this.selectedIndex, { instant: true });
    }
    if (this.adaptiveHeight) {
      this.#adaptHeight();
    }
    this.classList.toggle("ll-is-scrollable", this.scrollWidth > this.clientWidth);
  }
  #onMutate() {
    this.#forceChangeEvent = true;
  }
  #adaptHeight() {
    if (this.clientHeight === this.selectedCell.clientHeight) {
      return;
    }
    this.style.maxHeight = null;
    if (this.isScrollable) {
      this.style.maxHeight = `${this.selectedCell.clientHeight}px`;
    }
  }
  #preloadImages() {
    requestAnimationFrame(() => {
      const previousSlide = this.cells[Math.max(this.selectedIndex - 1, 0)], nextSlide = this.cells[Math.min(this.selectedIndex + 1, this.cells.length - 1)];
      [previousSlide, this.selectedCell, nextSlide].filter((item) => item !== null).forEach((item) => {
        Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
        Array.from(item.querySelectorAll('video[preload="none"]')).forEach((video) => video.setAttribute("preload", "metadata"));
      });
    });
  }
};
if (!window.customElements.get("scroll-carousel")) {
  window.customElements.define("scroll-carousel", ScrollCarousel);
}
export {
  EffectCarousel
};
